import Query from "@/models/query";

export function getAwsPresigner(
  path = "/aws/presigner",
  data = { prefix: "uploads" }
) {
  return Query.request(path, {
    method: "GET",
    params: data,
  });
}

export function getPublicAwsPresigner(
  data = { prefix: "uploads" }
) {
  return getAwsPresigner(
    "/aws/public_presigner",
    data
  );
}

function buildFormData(file, presigner) {
  const formData = new FormData();

  Object
    .keys(presigner.fields || {})
    .forEach((key) => {
      formData.append(
        key,
        presigner.fields[key]
      );
    });

  formData.append("file", file);

  return formData;
}

function parseXml(xhr) {
  if (xhr.responseXML) {
    return xhr.responseXML;
  }

  if (!xhr.responseText) {
    return null;
  }

  return new DOMParser().parseFromString(
    xhr.responseText,
    "application/xml"
  );
}

function xmlValue(document, tagName) {
  return document
    ?.getElementsByTagName(tagName)
    ?.[0]
    ?.textContent;
}

export function uploadFileToAmazonS3(
  file,
  presigner,
  {
    onProgress = () => { },
  } = {}
) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("File must be specified"));
      return;
    }

    if (!presigner?.url) {
      reject(new Error("Presigner must be specified"));
      return;
    }

    const xhr = new XMLHttpRequest();
    const formData = buildFormData(
      file,
      presigner
    );

    xhr.open(
      "POST",
      presigner.url,
      true
    );

    xhr.upload.addEventListener(
      "progress",
      onProgress,
      false
    );

    xhr.addEventListener(
      "load",
      () => {
        if (
          xhr.status < 200 ||
          xhr.status >= 300
        ) {
          reject(xhr);
          return;
        }

        const document = parseXml(xhr);

        resolve({
          document,
          url: xmlValue(
            document,
            "Location"
          ),
          awsKey: xmlValue(
            document,
            "Key"
          ),
          fileName: file.name,
        });
      },
      false
    );

    xhr.addEventListener(
      "error",
      () => reject(xhr),
      false
    );

    xhr.addEventListener(
      "abort",
      () => reject(xhr),
      false
    );

    xhr.send(formData);
  });
}

export default class AwsUploader {
  constructor(_props) {
    this._onUploadStart = () => { };
    this._onProgress = () => { };
    this._onSuccess = () => { };
    this._onError = () => { };
    this._presignerPath = "/aws/presigner";
    this._prefix = "uploads";
  }

  onUploadStart = (func) => {
    this._onUploadStart = func;
    return this;
  }

  onProgress = (func) => {
    this._onProgress = func;
    return this;
  }

  onSuccess = (func) => {
    this._onSuccess = func;
    return this;
  }

  onError = (func) => {
    this._onError = func;
    return this;
  }

  presignerPath = (
    path,
    prefix = "uploads"
  ) => {
    this._presignerPath = path;
    this._prefix = prefix;

    return this;
  }

  upload = async (file) => {
    if (!file) {
      this._onError(
        "File must be specified"
      );

      return;
    }

    if (file.size > 10485760) {
      this._onError(
        "Maximum file size of 10mb exceeded"
      );

      return;
    }

    const sanitizedFileName =
      file.name.replace(/[\s+]/g, "_");

    const sanitizedFile = new File(
      [file],
      sanitizedFileName,
      {
        type: file.type,
      }
    );

    try {
      const presigner =
        await getAwsPresigner(
          this._presignerPath,
          {
            prefix: this._prefix,
          }
        );

      if (!presigner) {
        this._onError(
          "Could not retrieve presigner"
        );

        return;
      }

      this._onUploadStart();

      const result =
        await uploadFileToAmazonS3(
          sanitizedFile,
          presigner,
          {
            onProgress:
              this.handleProgress,
          }
        );

      this._onSuccess({
        url: result.url,
        awsKey: result.awsKey,
        fileName: file.name,
      });

      return result;
    } catch (error) {
      this._onError(error);
      return undefined;
    }
  }

  handleProgress = (evt) => {
    if (!evt.lengthComputable) return;

    const percent =
      evt.loaded / evt.total;

    const percentComplete =
      parseInt(percent * 100);

    this._onProgress(
      percentComplete
    );
  }
}
